import React, { type ReactElement } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Route, Routes, useParams } from 'react-router-dom';
import { GlobalErrorBoundary } from './components/errors/GlobalErrorBoundary.tsx';
import { NotFoundPage } from './pages/errors/NotFound.tsx';
import { ForbiddenPage } from './pages/errors/Forbidden.tsx';
import { LandingPage } from './pages/landing.tsx';
import { BusinessPage } from './pages/business.tsx';
import { IndividualPage } from './pages/individual.tsx';
import { PricingPage } from './pages/pricing.tsx';
import { CatalogueMarketingPage } from './pages/catalogue.tsx';
import { AuthSwitch } from './components/auth/AuthSwitch.tsx';
import { SignInPage } from './pages/auth/sign-in.tsx';
import { SignUpPage } from './pages/auth/sign-up.tsx';
import { ForgotPasswordPage } from './pages/auth/forgot-password.tsx';
import { ResetPasswordPage } from './pages/auth/reset-password.tsx';
import { DashboardPage } from './pages/me/dashboard.tsx';
import { RoleBasedRedirect } from './components/auth/RoleBasedRedirect.tsx';
import { DashboardGuard } from './pages/me/DashboardGuard.tsx';
import { ResultPage } from './pages/attempts/result.tsx';
import { AdminQuestionListPage } from './pages/admin/questions/list.tsx';
import { AdminQuestionEditPage } from './pages/admin/questions/edit.tsx';
import { AdminQuestionImportPage } from './pages/admin/questions/import.tsx';
import { AdminTestListPage } from './pages/admin/tests/list.tsx';
import { AdminTestEditPage } from './pages/admin/tests/edit.tsx';
import { AssignmentsPage } from './pages/company/assignments/AssignmentsPage.tsx';
import { AttemptsPage } from './pages/company/attempts.tsx';
import { MembersPage } from './pages/company/members.tsx';
import { TeamReportPage } from './pages/company/team-report.tsx';
import { CompanyDashboardPage } from './pages/company/dashboard.tsx';
import { AdminAssignmentsPage } from './pages/admin/assignments/AdminAssignmentsPage.tsx';
import { AdminUserErasePage } from './pages/admin/users/erase.tsx';
import { AdminIssueTokenPage } from './pages/admin/users/issue-token.tsx';
import { MyDataPage } from './pages/me/data.tsx';
import { ActivityPage } from './pages/me/activity.tsx';
import { AdminCompaniesListPage } from './pages/admin/companies/list.tsx';
import { AdminCompanyDetailPage } from './pages/admin/companies/detail.tsx';
import { AdminShareTokenDetailPage } from './pages/admin/share-tokens/detail.tsx';
import { AdminDashboardPage } from './pages/admin/dashboard.tsx';
import { AdminTaxonomyPage } from './pages/admin/taxonomy/list.tsx';
import { SystemHealthPage } from './pages/admin/system-health.tsx';
import { AuditLogPage } from './pages/admin/audit-log.tsx';
import { ExtendedTimeGrantPage } from './pages/admin/users/extended-time-grant.tsx';
import { CataloguePage } from './pages/catalogue/CataloguePage.tsx';
import { TestDetailPage } from './pages/catalogue/TestDetailPage.tsx';
import { PreScreenPage } from './pages/take/prescreen.tsx';
import { PreviewPage } from './pages/tests/preview.tsx';
import QuestionPhasePage from './pages/take/question-phase.tsx';
import { AdminAttemptsPage } from './pages/admin/attempts.tsx';
import { ImpersonationBanner } from './components/admin/ImpersonationBanner.tsx';
import { ShareTokensPage } from './pages/share-tokens/ShareTokensPage.tsx';
import { CompanyCreatePage } from './pages/companies/new.tsx';
import { PrivacyPolicyPage } from './pages/legal/privacy.tsx';
import { TermsOfServicePage } from './pages/legal/terms.tsx';
import { CookiePolicyPage } from './pages/legal/cookies.tsx';
import { CookieConsentBanner } from './components/CookieConsentBanner.tsx';
import { StaleConsentModal } from './components/StaleConsentModal.tsx';
import { OfflineBanner } from './components/app-shell/OfflineBanner.tsx';
import { ReauthModal } from './components/auth/ReauthModal.tsx';
import { Toaster } from './components/ui/toast.tsx';
import './index.css';

function AdminQuestionEditRoute(): ReactElement {
  const params = useParams<{ id: string }>();
  return <AdminQuestionEditPage questionId={params.id} />;
}

function AdminTestEditRoute(): ReactElement {
  const params = useParams<{ id: string }>();
  return <AdminTestEditPage testId={params.id} />;
}

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('root element not found');
}

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <BrowserRouter>
      <GlobalErrorBoundary>
      <ImpersonationBanner />
      <OfflineBanner />
      <ReauthModal />
      {/* J-X14 — Cookie consent banner mounted at App root; renders only when
          the cookie_consent cookie is missing. Also owns the cookie-preferences
          modal triggered by the footer button. */}
      <CookieConsentBanner />
      {/* J-X13 — Blocking modal for authed users whose accepted ToS/Privacy
          version is stale. Anonymous responses are silently ignored. */}
      <StaleConsentModal />
      {/* J-X11 — Toast portal mounted at app root. All toast() calls render
          here via sonner's portal. */}
      <Toaster />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        {/* Marketing pages ported from bedd.dev snapshots. Both `/<name>` and
            `/<name>.html` route to the same component so old bedd.dev links
            don't 404. `/catalogue` is auth-aware — signed-out visitors see
            the marketing variant, signed-in members see the authenticated
            CataloguePage (see the dedicated /catalogue route below). The
            `/catalogue.html` alias always renders the marketing variant. */}
        <Route path="/business" element={<BusinessPage />} />
        <Route path="/business.html" element={<BusinessPage />} />
        <Route path="/individual" element={<IndividualPage />} />
        <Route path="/individual.html" element={<IndividualPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/pricing.html" element={<PricingPage />} />
        <Route path="/catalogue.html" element={<CatalogueMarketingPage />} />
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/sign-up" element={<SignUpPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/post-sign-in" element={<RoleBasedRedirect />} />
        <Route path="/dashboard" element={<DashboardGuard><DashboardPage /></DashboardGuard>} />
        <Route path="/companies/new" element={<DashboardGuard><CompanyCreatePage /></DashboardGuard>} />
        <Route path="/admin" element={<AdminDashboardPage />} />
        <Route path="/admin/companies" element={<AdminCompaniesListPage />} />
        <Route path="/admin/companies/:id" element={<AdminCompanyDetailPage />} />
        <Route path="/admin/share-tokens/:id" element={<AdminShareTokenDetailPage />} />
        <Route path="/admin/attempts" element={<AdminAttemptsPage />} />
        <Route path="/admin/questions" element={<AdminQuestionListPage />} />
        <Route path="/admin/questions/import" element={<AdminQuestionImportPage />} />
        <Route path="/admin/questions/new" element={<AdminQuestionEditPage />} />
        <Route path="/admin/questions/:id" element={<AdminQuestionEditRoute />} />
        <Route path="/admin/taxonomy" element={<AdminTaxonomyPage />} />
        <Route path="/admin/tests" element={<AdminTestListPage />} />
        <Route path="/admin/tests/new" element={<AdminTestEditPage />} />
        <Route path="/admin/tests/:id" element={<AdminTestEditRoute />} />
        <Route path="/company/assignments" element={<AssignmentsPage />} />
        <Route path="/company/:id/dashboard" element={<CompanyDashboardPage />} />
        <Route path="/company/:id/attempts" element={<AttemptsPage />} />
        <Route path="/company/:id/members" element={<MembersPage />} />
        <Route path="/company/:id/team-report" element={<TeamReportPage />} />
        <Route path="/company/:id/share-tokens" element={<ShareTokensPage />} />
        <Route path="/admin/assignments" element={<AdminAssignmentsPage />} />
        <Route path="/admin/users/erase" element={<AdminUserErasePage />} />
        <Route path="/admin/users/:id/issue-token" element={<AdminIssueTokenPage />} />
        <Route path="/admin/system-health" element={<SystemHealthPage />} />
        <Route path="/admin/audit-log" element={<AuditLogPage />} />
        <Route
          path="/admin/users/extended-time-grant"
          element={<ExtendedTimeGrantPage />}
        />
        <Route
          path="/catalogue"
          element={<AuthSwitch signedIn={<CataloguePage />} signedOut={<CatalogueMarketingPage />} />}
        />
        <Route path="/catalogue/:id" element={<TestDetailPage />} />
        <Route path="/take/:token" element={<PreScreenPage />} />
        <Route path="/take/:token/embed" element={<PreScreenPage embed />} />
        {/* Story 68 — admin preview-as-candidate route (TC/02 round 2) */}
        <Route path="/tests/preview/:attemptId" element={<PreviewPage />} />
        <Route path="/attempt/:id" element={<QuestionPhasePage />} />
        <Route path="/attempts/:id/result" element={<ResultPage />} />
        <Route path="/attempts/:id/result/embed" element={<ResultPage embed />} />
        <Route path="/me/data" element={<MyDataPage />} />
        {/* J-X23 — self-audit transparency */}
        <Route path="/me/activity" element={<ActivityPage />} />
        {/* J-X13 + J-X14 — public legal pages, reachable while signed-out */}
        <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/legal/terms" element={<TermsOfServicePage />} />
        <Route path="/legal/cookies" element={<CookiePolicyPage />} />
        {/* J-X8 — 403 explicit route */}
        <Route path="/forbidden" element={<ForbiddenPage />} />
        {/* J-X8 — catch-all 404; must be LAST in route list */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </GlobalErrorBoundary>
    </BrowserRouter>
  </React.StrictMode>,
);
